<script setup lang="ts">
/**
 * The buttons of the editor extensions (I-2799), each opening its panel in a
 * popover beside it or a window of its own — or, for an extension without a
 * panel (the comment), doing its work at once. `NxRichTextToolbar` shows them
 * after its insert tools; a host without a toolbar — Mail's plain-text mode —
 * shows them itself and hands in what the panel gets (`context`). The place
 * around (`provideRichTextPlace`) joins that context here, so no host has to.
 */
import { ref, shallowRef } from 'vue';
import { NxButton } from '../Button';
import { NxIconButton } from '../IconButton';
import { NxOfficeIcon } from '../OfficeRibbon';
import { NxModal } from '../Modal';
import { NxPopover } from '../Popover';
import { NxTooltip } from '../Tooltip';
import {
  useRichTextPlace,
  type NxRichTextExtension,
  type NxRichTextExtensionContext,
} from './extensions';

const props = defineProps<{
  extensions: readonly NxRichTextExtension[];
  /** What a panel gets, read when it opens (format, selection, placeholders, insert). */
  context: () => Omit<NxRichTextExtensionContext, 'close'>;
}>();

const emit = defineEmits<{
  /** A panel closed itself; the host puts the caret back into the text. */
  closed: [];
}>();

const openId = ref<string | null>(null);
const contextNow = shallowRef<NxRichTextExtensionContext | null>(null);
const place = useRichTextPlace();

function contextFor(): NxRichTextExtensionContext {
  const host = props.context();
  return { ...host, place: host.place ?? place.value, close: () => close(true) };
}

/**
 * Opens an extension's panel, or does an extension without one; false when
 * there is no such extension.
 */
function open(id: string): boolean {
  const extension = props.extensions.find((candidate) => candidate.id === id);
  if (!extension) return false;
  if (extension.run) {
    // Done at once: the host puts the caret back afterwards, as after a panel.
    void Promise.resolve(extension.run(contextFor())).finally(() => emit('closed'));
    return true;
  }
  contextNow.value = contextFor();
  openId.value = id;
  return true;
}

/** A window gives the page back only once it has closed; `closed` waits for that. */
let closedAfterWindow = false;

function close(back = false): void {
  if (openId.value === null) return;
  const inWindow =
    props.extensions.find((extension) => extension.id === openId.value)?.presentation === 'dialog';
  openId.value = null;
  contextNow.value = null;
  if (!back) return;
  // The host puts the focus back into its editor. While the modal window is
  // still open the editor is inert, and closing the window would then hand the
  // focus to this button: the host hears of it after the window has closed.
  if (inWindow) closedAfterWindow = true;
  else emit('closed');
}

function onWindowClosed(): void {
  if (!closedAfterWindow) return;
  closedAfterWindow = false;
  emit('closed');
}

function onOpen(id: string, wanted: boolean): void {
  if (wanted) open(id);
  else if (openId.value === id) close();
}

const title = (extension: NxRichTextExtension) =>
  extension.shortcut ? `${extension.label} (${extension.shortcut})` : extension.label;

defineExpose({ open, close });
</script>

<template>
  <template v-for="extension in extensions" :key="extension.id">
    <!-- No panel: the button does it at once. -->
    <NxTooltip v-if="extension.run" :text="title(extension)">
      <NxIconButton
        v-if="extension.icon"
        height="xs"
        :data-extension="extension.id"
        :aria-label="extension.label"
        @click="open(extension.id)"
      >
        <NxOfficeIcon :name="extension.icon" />
      </NxIconButton>
      <NxButton
        v-else
        height="xs"
        variant="ghost"
        :data-extension="extension.id"
        @click="open(extension.id)"
      >
        {{ extension.label }}
      </NxButton>
    </NxTooltip>
    <!-- A panel with much to show opens in a window of its own. -->
    <template v-else-if="extension.presentation === 'dialog'">
      <NxTooltip :text="title(extension)">
        <NxIconButton
          v-if="extension.icon"
          height="xs"
          :data-extension="extension.id"
          :pressed="openId === extension.id"
          :aria-label="extension.label"
          @click="open(extension.id)"
        >
          <NxOfficeIcon :name="extension.icon" />
        </NxIconButton>
        <NxButton
          v-else
          height="xs"
          variant="ghost"
          :data-extension="extension.id"
          :aria-pressed="openId === extension.id"
          @click="open(extension.id)"
        >
          {{ extension.label }}
        </NxButton>
      </NxTooltip>
      <NxModal
        :open="openId === extension.id"
        :close-on-click-outside="false"
        :storage-key="extension.id"
        @update:open="(wanted: boolean) => !wanted && close(true)"
        @modal-closed="onWindowClosed"
      >
        <template #header>{{ extension.label }}</template>
        <template #body>
          <component
            :is="extension.panel"
            v-if="openId === extension.id && contextNow"
            :context="contextNow"
          />
        </template>
      </NxModal>
    </template>
    <NxPopover
      v-else
      :open="openId === extension.id"
      side="bottom-start"
      :label="extension.label"
      @update:open="(wanted: boolean) => onOpen(extension.id, wanted)"
    >
      <template #trigger>
        <!-- An icon stands in the row like every other tool; the tooltip names it
             (with its shortcut), here as well as in a host's own bar. -->
        <NxTooltip :text="title(extension)">
          <NxIconButton
            v-if="extension.icon"
            height="xs"
            :data-extension="extension.id"
            :pressed="openId === extension.id"
            :aria-label="extension.label"
          >
            <NxOfficeIcon :name="extension.icon" />
          </NxIconButton>
          <NxButton
            v-else
            height="xs"
            variant="ghost"
            :data-extension="extension.id"
            :aria-pressed="openId === extension.id"
          >
            {{ extension.label }}
          </NxButton>
        </NxTooltip>
      </template>
      <component
        :is="extension.panel"
        v-if="openId === extension.id && contextNow"
        :context="contextNow"
      />
    </NxPopover>
  </template>
</template>
